import { useEffect, useRef } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { cn } from '@/utils/cn'
import { Home, FolderOpen, Gift, Video, Image, Film, CreditCard, Crown, LogIn, UserPlus, X, HelpCircle } from 'lucide-react'
import { useRedirect } from '@/contexts/redirect-context'

interface MobileMenuProps {
  isOpen: boolean
  onClose: () => void
}

interface NavItem {
  icon: React.ElementType
  label: string
  href: string
  isSpecial?: boolean
  isExternal?: boolean
  requiresPlan?: boolean
}

const mainNavItems: NavItem[] = [
  { icon: Home, label: 'Home', href: '/' },
  { icon: FolderOpen, label: 'Assets', href: '/assets', isExternal: true },
  { icon: Gift, label: 'Daily Rewards', href: '/rewards', isExternal: true, requiresPlan: true },
  { icon: CreditCard, label: 'Upgrade', href: '/billing', isSpecial: true, isExternal: true },
]

const createNavItems: NavItem[] = [
  { icon: Video, label: 'AI Video', href: '/video' },
  { icon: Image, label: 'AI Images', href: '/images', isExternal: true },
]

const utilitiesNavItems: NavItem[] = [
  { icon: Film, label: 'Extend Video', href: '/extend-video', isExternal: true, requiresPlan: true },
]

export function MobileMenu({ isOpen, onClose }: MobileMenuProps) {
  const location = useLocation()
  const prevPathRef = useRef(location.pathname)

  // Close menu on route change
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname
      if (isOpen) onClose()
    }
  }, [location.pathname, isOpen, onClose])

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity duration-300 md:hidden',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
      />

      {/* Bottom Sheet */}
      <div
        className={cn(
          'fixed inset-x-0 bottom-0 z-50 bg-[#0a0a0a] rounded-t-2xl border-t border-white/[0.1] transition-transform duration-300 ease-out md:hidden',
          isOpen ? 'translate-y-0' : 'translate-y-full'
        )}
        style={{ maxHeight: '85vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
          <h2 className="text-lg font-semibold text-white">Navigation</h2>
          <button
            onClick={onClose}
            className="p-2 -mr-2 rounded-lg text-[#888] hover:text-white hover:bg-white/[0.05] transition-colors"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 pb-8 space-y-6 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 60px)' }}>
          {/* Main Nav */}
          <NavSection items={mainNavItems} onClose={onClose} />

          {/* Create */}
          <NavSection items={createNavItems} label="Create" onClose={onClose} />

          {/* Utilities */}
          <NavSection items={utilitiesNavItems} label="Utilities" onClose={onClose} />

          {/* Support & Auth Buttons */}
          <AuthButtons onClose={onClose} />
        </div>
      </div>
    </>
  )
}

interface NavSectionProps {
  items: NavItem[]
  label?: string
  onClose: () => void
}

function NavSection({ items, label, onClose }: NavSectionProps) {
  return (
    <div className="space-y-1.5">
      {label && (
        <div className="pb-2 text-xs font-medium text-[#555] uppercase tracking-wider">
          {label}
        </div>
      )}
      {items.map((item) => (
        <MobileNavLink key={item.href} item={item} onClose={onClose} />
      ))}
    </div>
  )
}

interface MobileNavLinkProps {
  item: NavItem
  onClose: () => void
}

function MobileNavLink({ item, onClose }: MobileNavLinkProps) {
  const Icon = item.icon
  const location = useLocation()
  const { openRedirect } = useRedirect()
  const isActive = item.href === '/' ? location.pathname === '/' : location.pathname.startsWith(item.href)

  // External links (redirect to pixelmaker.ai with modal)
  if (item.isExternal) {
    const handleClick = () => {
      onClose()
      const path = item.href === '/billing' ? '/billing' : item.href
      openRedirect(path)
    }

    if (item.isSpecial) {
      return (
        <button
          onClick={handleClick}
          className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 bg-gradient-to-r from-violet-600/60 via-violet-400/60 to-violet-600/60 border border-violet-400/50 text-white shadow-[0_0_12px_rgba(139,92,246,0.2)]"
        >
          <Icon className="size-5" />
          {item.label}
        </button>
      )
    }

    return (
      <button
        onClick={handleClick}
        className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm font-medium text-left transition-all duration-200 text-[#666] border border-transparent active:bg-white/[0.04]"
      >
        <Icon className="size-5" />
        <span className="flex-1 text-left">{item.label}</span>
        {item.requiresPlan && (
          <Crown className="size-4 text-amber-400" />
        )}
      </button>
    )
  }

  // Internal links
  if (item.isSpecial) {
    return (
      <NavLink
        to={item.href}
        end={item.href === '/'}
        onClick={onClose}
        className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 bg-gradient-to-r from-violet-600/60 via-violet-400/60 to-violet-600/60 border border-violet-400/50 text-white shadow-[0_0_12px_rgba(139,92,246,0.2)]"
      >
        <Icon className="size-5" />
        {item.label}
      </NavLink>
    )
  }

  return (
    <NavLink
      to={item.href}
      end={item.href === '/'}
      onClick={onClose}
      className={cn(
        'flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200',
        isActive 
          ? 'text-white bg-white/[0.05] border border-white/[0.1]' 
          : 'text-[#666] border border-transparent active:bg-white/[0.04]'
      )}
    >
      <Icon className="size-5" />
      <span className="flex-1">{item.label}</span>
      {item.requiresPlan && (
        <Crown className="size-4 text-amber-400" />
      )}
    </NavLink>
  )
}

function AuthButtons({ onClose }: { onClose: () => void }) {
  const { openRedirect } = useRedirect()

  const handleSignIn = () => {
    onClose()
    openRedirect()
  }

  const handleJoinFree = () => {
    onClose()
    openRedirect()
  }

  const handleSupport = () => {
    onClose()
    openRedirect('/support')
  }

  return (
    <div className="space-y-3">
      {/* Support */}
      <button
        onClick={handleSupport}
        className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm font-medium text-left transition-all duration-200 text-[#666] border border-transparent active:bg-white/[0.04]"
      >
        <HelpCircle className="size-5" />
        Support
      </button>

      <div className="pb-1 text-xs font-medium text-[#555] uppercase tracking-wider">
        Get Started
      </div>
      <div className="flex gap-3">
        <button
          onClick={handleSignIn}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium text-white bg-white/[0.05] border border-white/[0.1] active:bg-white/[0.08] transition-colors"
        >
          <LogIn className="size-4" />
          Log In
        </button>
        <button
          onClick={handleJoinFree}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium text-black bg-white active:bg-white/90 transition-colors"
        >
          <UserPlus className="size-4" />
          Join Free
        </button>
      </div>
    </div>
  )
}
